import { Button, Icon, IconButton } from "@material-ui/core";
import { CheckCircleRounded } from "@material-ui/icons";
import firebase from "firebase/compat/app";
import CalendarRange from "mdi-material-ui/CalendarRange";
import CloudSearch from "mdi-material-ui/CloudSearch";
import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Update from "mdi-material-ui/Update";
import React, { Component } from "react";
import { Transition } from "react-transition-group";
import styled from "styled-components";

import {
    LibraryView,
    PresentationFilters
} from "common/constants";
import { ActionPermissionsObject, PermissionActionType, WorkspacePlanTier, WorkspaceResourceFolderType, WorkspaceResourceType } from "common/interfaces";
import { Button as BAIButton } from "js/Components/Button";
import InfoBanner from "js/Components/InfoBanner";
import { getStaticUrl } from "js/config";
import PresentationLibraryController, { PresentationFilterType, PresentationLibraryControllerState, PresentationLibraryFilter, PresentationLibraryPresentation, PresentationLibrarySort } from "js/controllers/PresentationLibraryController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { User } from "js/core/models/user";
import isConnected from "js/core/utilities/isConnected";
import { delay } from "js/core/utilities/promiseHelper";
import { OnMountedReporter } from "js/core/utilities/useMountedAt";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { Divider } from "js/Components/Divider";
import { AiIcon, AiIconCircle } from "js/editor/AiGeneration/AiIcon";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import ShortcutPanel from "js/editor/PresentationEditor/Components/ShortCutPanel";
import LibrarySkeleton from "js/editor/PresentationLibrary/components/LibrarySkeleton";
import { buildFolderPathFolders } from "js/editor/PresentationLibrary/utils/folderPaths";
import { PresentationLibraryGrid } from "js/editor/PresentationLibrary/PresentationLibraryGrid";
import { PresentationLibraryList } from "js/editor/PresentationLibrary/PresentationLibraryList";
import { PresentationLibraryShareNoticeMessage } from "js/editor/PresentationLibrary/PresentationLibraryShareNoticeMessage";
import { PRESENTATION_LIBRARY_VIEW_SELECT_WIDTH } from "js/editor/PresentationLibrary/PresentationLibrarySideBarLayout";
import { PresentationLibrarySideBar } from "js/editor/PresentationLibrary/PresentationLibrarySideBar";
import { PresentationListHeader } from "js/editor/PresentationLibrary/PresentationListHeader";
import { app } from "js/namespaces";
import {
    ShowDialog,
    ShowConfirmationDialog,
    ShowErrorDialog,
    ShowInputDialog,
    ShowMessageDialog,
    ShowOfflineDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { UpgradeToTeamPlanDialog } from "js/react/components/Dialogs/UpgradeToTeamPlanDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { FlexSpacer, Gap10, Gap20, Gap30, Gap5 } from "js/react/components/Gap";
import { FlexBox, ScrollBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, Notice, ResourceNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import {
    StyledWarningIcon,
    BlueButton,
    UIPane,
    UIPaneContents,
    UIPaneSideBar,
    UIPaneWithSideBar,
    WarningBox,
    WarningButton
} from "js/react/components/UiComponents";
import { browserHistory } from "js/react/history";
import { themeColors } from "js/react/sharedStyles";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { t } from "js/i18n";
const logger = getLogger(LogGroup.LIBRARY);

const CloseButtonContainer = styled.div < { isMobile?: boolean } > `
    text-align: right;
    margin-bottom: -15px;
    margin-top: 10px;
    padding: ${props => props.isMobile ? "0 20px" : "0"};
`;

const RemoteControlBanner = styled.div<{ blue?: boolean }>`
    padding: 10px;
    text-align: center;
    text-wrap: nowrap;
    background: ${props => props.blue ? themeColors.ui_blue : "orangered"};
    color: white;
    font-size: 18px;
    width: 100%;
    font-weight: 600;
`;

const SubFolderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    max-height: 108px;
    overflow-y: auto;
    gap: 5px;
    row-gap: 5px;
`;

const DesktopLibraryContentStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    height: 100%;
    min-height: 0;
`;

const SubFolder = styled.div<{ isSelected: boolean }>`
    background: ${props => props.isSelected ? "white" : "#e1e1e1"};
    padding: 10px 15px 10px 10px;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    span {
        font-weight: 400;
    }

    .MuiIcon-root {
        opacity: 0.5;
    }

    &:hover {
        background: ${themeColors.selection};
    }
`;

const CreatePresentationButton = styled(Button)`
    flex-shrink: 0;
    height: 50px;
    background: ${themeColors.ui_blue} !important;
    padding: 0px 20px 0px 10px !important;

    .MuiButton-label {
        color: white;
        font-family: "Source Sans Pro";
        font-size: 16px;
        font-weight: 600;
        margin-left: 10px;
    }

    .MuiIcon-root {
        background: white;
        color: black;
        border-radius: 50%;
        padding: 3px;
        font-size: 17px;
        margin-right: 6px;
    }
`;

const CreatePresentationButtonIntegrator = styled(CreatePresentationButton)`
    border: 1px solid ${themeColors.ui_blue} !important;
    padding: 0 !important;
    margin-right: 14px !important;

    .MuiButton-label {
        color: #444;
        background: transparent;
        padding: 10px 20px;
    }

    .dark-mode & {
        .MuiButton-label {
            color: #ccc;
        }
    }
`;

const CreatePresentationButtonContainer = styled.div`
    position: relative;
    flex-shrink: 0;
`;

const CreateFolderButton = styled(Button)`
    &&& {
        background: ${themeColors.lightGray};
    } 
`;

const PopupMenuMask = styled.div<{ state: string }>`
    position: absolute;
    top: calc(100% + 5px);
    right: 0;
    z-index: 2000;
`;

const CreatePopupMenu = styled.div<{ state: string }>`
    padding: 10px;
    background: white;
    border-radius: 2px;
    color: black;
    flex-direction: column;
    z-index: 1000;
    display: flex;
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.2);
    align-items: center;
    opacity: ${({ state }) => (["entering", "entered"].includes(state) ? 1 : 0)};
    display: ${({ state }) => (["entering", "entered"].includes(state) ? "block" : "none")};
    transition: 300ms;
`;

const CreatePopupMenuItem = styled.div<{ disabled?: boolean }>`
    font-size: 14px;
    font-weight: 600;
    gap: 10px;
    white-space: nowrap;
    color: ${({ disabled }) => disabled ? themeColors.gray : themeColors.darkGray};
    display: flex;
    align-items: center;
    padding: 10px;
    width: 100%;
    cursor: pointer;

    &:hover {
        background: #f1f1f1;
    }

    .MuiIcon-root {
        font-size: 18px;
        color: ${({ disabled }) => disabled ? themeColors.gray : themeColors.mediumGray};
    }
`;

const StyledBlueButton = styled(BlueButton as any)`
    &&& {
        height: 100%;
        padding: 6px 20px;
        border-radius: 0;
    }
`;

export const Breadcrumb = styled.div`
    color: #000;
    text-align: right;
    /* popup/heading */
    font-family: "Source Sans Pro";
    font-size: 23px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%; /* 27.6px */
    letter-spacing: 0.5px;

    .dark-mode & {
        color: #ccc;
    }
`;

const HeaderTitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

const HeaderPath = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
`;

const HeaderPathLink = styled.button<{ isCurrent?: boolean }>`
    background: none;
    border: 0;
    padding: 0;
    color: ${({ isCurrent }) =>
        isCurrent ? themeColors.darkGray : themeColors.mediumGray};

    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

const HeaderTitle = styled(Breadcrumb)`
    text-align: left;
    text-transform: lowercase;

    &::first-letter {
        text-transform: uppercase;
    }
`;

const StyledRoundedIcon = styled(CheckCircleRounded)`
    &.MuiSvgIcon-colorPrimary {
        width: 16px;
        height: 16px;
        margin-right: 8px;
    }
`;

const StyledPresentationListHeader = styled(PresentationListHeader)`
    width: 100%;
`;

const ButtonContent = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StyledBAIButton = styled(BAIButton)`
    height: auto;
    width: 100%;
    padding: 4px 12px;

    span {
        padding: 6px 0;
        gap: 10px;
        height: auto;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const InfoBannerContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 20px 0 20px;
`;

const HiddenScrollBox = styled(ScrollBox)`
    /* Hide scrollbar for WebKit browsers (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
        display: none;
    }

    /* Hide scrollbar for Firefox */
    scrollbar-width: none;

    /* Hide scrollbar for IE and Edge Legacy */
    -ms-overflow-style: none;
`;

const StyledRenderItemsContainer = styled.div`
    padding: 0 20px;
    box-sizing: border-box;
`;

type PresentationLibraryPaneProps = PresentationLibraryControllerState & WorkspaceControllerState & {
    workspaceId: string;
    firebaseUser: firebase.User;
    readOnly?: boolean;
    pane?: PresentationFilterType;
    folderId?: string;
    subFolderId?: string;
    user: typeof User;
    onSelectedPresentation: (presentationId: string) => void;
    onCloseDialog: () => void;
    showCloseIcon: boolean;
    showSidebar: boolean;
    onHideSidebar: () => void;
    onSwitchType: (view: string) => void;
    sideBarWidth: number;
    isSideBarContentVisible: boolean;
    onResizeSidebar: (width: number) => void;
    className: string;
    showLibraryViewSelect: boolean;
    isMyPresentationsOnly: boolean;
    onReady?: () => void;
    onDoubleClick?: () => void;
}

interface InnerPresentationLibraryPaneState {
    draggedItems: PresentationLibraryPresentation[];
    infoBannerClosed: boolean;
    showCreateMenu: "on" | "timeout" | null;
}

// Base class with shared logic
export class PresentationLibraryPaneBase extends Component<PresentationLibraryPaneProps, InnerPresentationLibraryPaneState> {
    sideBarRef: React.RefObject<any>;
    scrollRef: React.RefObject<any>;

    get sortOptions() {
        const { searchQuery } = this.props;

        const options = [{
            value: "name", label: t("Name"), reverse: false, icon: <SortAlphabeticalVariant />
        }, {
            value: "createdAt", label: t("Recently Created"), reverse: true, icon: <CalendarRange />
        }, {
            value: "modifiedAt", label: t("Recently Edited"), reverse: true, icon: <Update />
        }];
        if (searchQuery) {
            options.push({
                value: "relevance", label: t("Relevance"), reverse: false, icon: <CloudSearch />
            });
        }
        return options;
    }

    constructor(props) {
        super(props);

        this.sideBarRef = React.createRef();
        this.scrollRef = React.createRef();

        this.state = {
            draggedItems: [],
            infoBannerClosed: false,
            showCreateMenu: null
        };
    }

    handleInfoBannerClose = () => {
        localStorage.setItem("bai-mobile-info-banner-closed", "true");
        this.setState({ infoBannerClosed: true });
    }

    componentDidMount() {
        if (localStorage.getItem("bai-mobile-info-banner-closed") === "true") {
            this.setState({ infoBannerClosed: true });
        }
    }

    componentWillUnmount() {
        PresentationLibraryController.setSearchQuery("");
        PresentationLibraryController.disposeSearchIndex();
    }

    handleSetViewType = (viewType: "grid" | "list") => {
        PresentationLibraryController.setViewType(viewType);
    }

    handleSearch = (query: string) => {
        PresentationLibraryController.setSearchQuery(query);
    }

    handleSearchBarOpen = () => {
        PresentationLibraryController.ensureSearchIndexPrepared();
    }

    handleSort = (sort: PresentationLibrarySort) => {
        PresentationLibraryController.setSort(sort);
    }

    handleSetFilter = (filter: PresentationLibraryFilter) => {
        const { readOnly } = this.props;

        // if we are in read-only (We're adding a slide from My Slide sectio ) mode
        // we should just update the filter state because we
        // don't want to navigate away from the current page
        if (readOnly) {
            PresentationLibraryController.setFilter(filter);
        } else {
            AppController.showLibrary({ filter });
        }
    }

    handleSelectedPresentations = async (selection: PresentationLibraryPresentation[]) => {
        const { onSelectedPresentation } = this.props;

        if (selection.length === 0) {
            return;
        }

        if (onSelectedPresentation) {
            onSelectedPresentation(selection[0].id);
            return;
        }

        if (app.isConstrained) {
            await this.handleDoubleClickedPresentation(selection[0]);
        }
    }

    handleDoubleClickedPresentation = async (presentation: PresentationLibraryPresentation) => {
        if (this.props.onDoubleClick) {
            return this.props.onDoubleClick();
        }

        if (presentation.trashedAt) {
            return ShowErrorDialog({
                title: t("Can't edit presentations in the trash"),
                message: t("Please restore this presentation back into your library before editing.")
            });
        }

        // zoom app cases (meeting and non-meeting views)
        if (window.isZoomApp) {
            if (window.roomID && window.roomPresentationStarted) {
                return await window.roomPresentationStarted(presentation.id)
                    .catch(err => ShowErrorDialog({
                        title: t("Error Starting Presentation"),
                        message: err.message
                    }));
            }

            // @ts-ignore
            if (!window.roomID && window.roomPresentationPreviewed) {
                // @ts-ignore
                return await window.roomPresentationPreviewed(presentation.id)
                    .catch(err => ShowErrorDialog({
                        title: t("Error Previewing Presentation"),
                        message: err.message
                    }));
            }
        }

        // show player if in a Meeting Room
        if (window.roomID) {
            PresentationLibraryController.playPresentation(presentation.id);
            return;
        }

        // show player if user doesn't have write permissions or if the app is constrained mode
        if (!presentation.permissions.write || app.isConstrained) {
            PresentationLibraryController.playPresentation(presentation.id, !app.isConstrained);
            return;
        }

        // show presentation editor
        AppController.showEditor({ presentationId: presentation.id });
    }

    handleStartDragItems = (selection: PresentationLibraryPresentation[]) => {
        this.setState({ draggedItems: selection });
    }

    handleDropDragItems = () => {
        this.setState({ draggedItems: [] });
    }

    handleSubFolderMouseUp = async (subFolderId: string) => {
        const { filter } = this.props;
        const { draggedItems } = this.state;
        const filterType = filter.type === PresentationFilterType.FOLDER ? PresentationFilterType.FOLDER : PresentationFilterType.TEAM_FOLDER;

        if (draggedItems?.length) {
            const resources = draggedItems.map(presentation => ({
                resourceId: presentation.id,
                resourceType: WorkspaceResourceType.PRESENTATION,
                permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
            }));

            try {
                await withProgressDialog({
                    title: t("Adding {{count}} presentations to folder...", { count: resources.length }),
                    action: async () => {
                        await WorkspaceController.updateWorkspaceResourceFolderResources({
                            folderId: subFolderId,
                            update: {
                                add: resources,
                            }
                        });

                        this.handleSetFilter({
                            type: filterType,
                            folderId: filter.folderId,
                            subFolderId
                        });
                    }
                });
            } catch (err) {
                logger.error(err, "Error adding presentations to folder");

                ShowErrorDialog({
                    title: t("Error adding presentations to folder"),
                    message: err.message
                });
            }
        } else {
            this.handleSetFilter({
                type: filterType,
                folderId: filter.folderId,
                subFolderId
            });
        }
    }

    handleEmptyTrash = () => {
        const { presentations, workspaceId } = this.props;

        const trashedPresentations = presentations.filter(p => p.trashedAt && p.workspaceId === workspaceId);
        if (trashedPresentations.length === 0) {
            return;
        }

        ShowConfirmationDialog({
            title: t("Are you sure you want to permanently delete all of the presentations in the trash?"),
            message: t("You can't undo this action."),
            acceptCallback: () => {
                PresentationLibraryController.deletePresentations(trashedPresentations.map(p => p.id));
            }
        });
    }

    handleMouseEnterCreatePresentationButton = () => {
        this.setState({ showCreateMenu: "on" });
    }

    handleMouseLeaveCreatePresentationButton = async () => {
        this.setState({ showCreateMenu: "timeout" });
        await delay(600);
        if (this.state.showCreateMenu === "timeout") {
            this.setState({ showCreateMenu: null });
        }
    }

    handleCreateNewPresentationFromHeader = async (pane = CreatePresentationDialogPaneType.BLANK_PRESENTATION) => {
        const { filter, filterMetadata: { permissions } } = this.props;
        const canUpgradeToCreateResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE);
        const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);

        if (canUpgradeToCreateResources) {
            ShowDialog(ChangePlanDialog, {
                featuredPlan: pane === CreatePresentationDialogPaneType.TEAM_TEMPLATES ? WorkspacePlanTier.TEAM : WorkspacePlanTier.PRO,
                analytics: { cta: "create_new_presentation" }
            });
            return;
        }

        if (!isConnected.connected) {
            ShowOfflineDialog();
            return;
        }

        if (filter.type === PresentationFilterType.TEAM_FOLDER) {
            if (!permissions.write.object) {
                ShowWarningDialog({
                    title: t("Permission denied"),
                    message: t("You don't have permissions to create presentations in this team folder. Contact your Team Admin to request access.")
                });
                return;
            }

            if (canUpgradeToUseSharedResources) {
                ShowDialog(UpgradeToTeamPlanDialog);
                return;
            }

            const confirmed = await ShowMessageDialog({
                title: t("Warning"),
                message: t("You are creating a presentation in a team folder. The members of the team folder will be able to view and edit the presentation."),
                showCancel: true,
                optOutProperty: "createPresentationInTeamFolderWarning",
                buttonOptions: {
                    color: "blue"
                }
            });
            if (!confirmed) {
                return;
            }
        }

        if (app.integrator) {
            window.open("/", "_blank");
            return;
        }

        AppController.showCreatePresentationDialog({
            pane,
            libraryFilter: {
                type: filter.type,
                subFolderId: filter.subFolderId,
                folderId: filter.folderId
            }
        });
    }

    handleCreateSubFolder = async () => {
        const { filter, filterMetadata: { permissions }, resourceFolders } = this.props;
        const currentFolder = resourceFolders.find(folder => folder.id === (filter.subFolderId ?? filter.folderId));
        const isTeamFolder = filter.type === PresentationFilterType.TEAM_FOLDER;
        const isPersonalFolder = filter.type === PresentationFilterType.FOLDER;
        const canUpgradeToCreateTeamSubfolder = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
        const canCreatePersonalSubfolder = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
        const canUpgradeToCreatePersonalSubfolder = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE);

        if (!currentFolder || currentFolder.parentFolderId || !filter.folderId || (!isTeamFolder && !isPersonalFolder)) {
            return;
        }

        if (isTeamFolder && (!permissions.write.object || canUpgradeToCreateTeamSubfolder)) {
            return;
        }

        if (isPersonalFolder && (!canCreatePersonalSubfolder || canUpgradeToCreatePersonalSubfolder)) {
            return;
        }

        const name = await ShowInputDialog({
            title: t("New folder"),
            maxChars: 100,
            value: t("New Folder")
        });

        if (!name) {
            return;
        }

        try {
            await withProgressDialog({
                title: t("Creating subfolder..."),
                action: () => WorkspaceController.createWorkspaceResourceFolder({
                    parentFolderId: filter.folderId,
                    name,
                    resourcesType: WorkspaceResourceType.PRESENTATION,
                    folderType: isTeamFolder
                        ? WorkspaceResourceFolderType.SHARED
                        : WorkspaceResourceFolderType.PERSONAL
                })
            });
        } catch (err) {
            logger.error(err, "[PresentationLibraryPane] handleCreateSubFolder() Unable to create subfolder");

            ShowErrorDialog({
                title: t("Unable to create subfolder"),
                message: err.message
            });
        }
    }

    getFilterData() {
        const { filter, resourceFolders, resources } = this.props;

        let subFolders = [];
        let title;
        let folderPathFolders = [];

        switch (filter.type) {
            case PresentationFilters.ALL_PRESENTATIONS:
                title = t("All Presentations");
                break;
            case PresentationFilters.RECENT:
                title = t("Recent Presentations");
                break;
            case PresentationFilters.OWNED_BY_ME:
                title = t("Created by me");
                break;
            case PresentationFilters.SHARED_WITH_ME:
                title = t("Shared With Me");
                break;
            case PresentationFilters.FOLDER:
            case PresentationFilters.TEAM_FOLDER: {
                const folderId = filter.subFolderId || filter.folderId;
                const folder = resourceFolders.find(f => f.id === folderId);
                if (folder) {
                    folderPathFolders = buildFolderPathFolders(folderId, resourceFolders);
                    title = folder.name;
                    subFolders = resourceFolders.filter(f => f.parentFolderId === folder.id);
                }
                break;
            }
            case PresentationFilters.TRASH:
                title = t("Trash");
                break;
        }

        return { title, subFolders, folderPathFolders };
    }

    getFolderFilterFromPathFolder(folder) {
        const isTeamFolder =
            folder.folderType === WorkspaceResourceFolderType.SHARED ||
            folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;

        return {
            type: isTeamFolder ? PresentationFilterType.TEAM_FOLDER : PresentationFilterType.FOLDER,
            folderId: folder.parentFolderId ?? folder.id,
            subFolderId: folder.parentFolderId ? folder.id : undefined,
        };
    }

    renderHeaderLeadingContent(title, folderPathFolders = []) {
        if (!title) {
            return null;
        }

        return (
            <HeaderTitleBlock>
                {folderPathFolders.length > 1 && (
                    <HeaderPath>
                        {folderPathFolders.map((folder, index) => {
                            const isCurrent = index === folderPathFolders.length - 1;

                            return (
                                <React.Fragment key={folder.id}>
                                    {index > 0 && (
                                        <Icon style={{ fontSize: 20, color: themeColors.darkGray }}>
                                            chevron_right
                                        </Icon>
                                    )}
                                    <HeaderPathLink
                                        type="button"
                                        isCurrent={isCurrent}
                                        onClick={() => this.handleSetFilter(this.getFolderFilterFromPathFolder(folder))}
                                    >
                                        {folder.name}
                                    </HeaderPathLink>
                                </React.Fragment>
                            );
                        })}
                    </HeaderPath>
                )}
                <HeaderTitle>{title}</HeaderTitle>
            </HeaderTitleBlock>
        );
    }

    renderHeaderAction() {
        const { filter, readOnly, filterMetadata: { permissions }, resourceFolders } = this.props;
        const { showCreateMenu } = this.state;
        const designerBotAvailable = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use;
        const canUpgradeToUseDesignerBot = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, PermissionActionType.USE);
        const canCreateResources = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCES].create;
        const canUpgradeToCreateResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE);
        const starterTemplatesAvailable = WorkspaceController.actionPermissions[ActionPermissionsObject.INSPIRATION_SLIDES].use;
        const sharedResourcesAvailable = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
        const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);
        const currentFolder = resourceFolders.find(folder => folder.id === (filter.subFolderId ?? filter.folderId));
        const canUpgradeToCreateTeamSubfolder = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
        const canCreatePersonalSubfolder = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
        const canUpgradeToCreatePersonalSubfolder = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE);
        const canCreateSubfolder = !!currentFolder && !currentFolder.parentFolderId && (
            (filter.type === PresentationFilterType.TEAM_FOLDER && permissions.write.object && !canUpgradeToCreateTeamSubfolder) ||
            (filter.type === PresentationFilterType.FOLDER && canCreatePersonalSubfolder && !canUpgradeToCreatePersonalSubfolder)
        );

        if (readOnly || filter.type === PresentationFilters.TRASH) {
            return null;
        }

        if (app.integrator) {
            return (
                <CreatePresentationButtonIntegrator
                    id="new-presentation-button-pane"
                    variant="text"
                    onClick={() => this.handleCreateNewPresentationFromHeader()}
                >
                    <Icon>add</Icon>{t("Create New Presentation")}
                </CreatePresentationButtonIntegrator>
            );
        }

        if (!canCreateResources) {
            return null;
        }

        if (app.isMobileOrTablet) {
            return canCreateSubfolder ? (
                <CreateFolderButton
                    id="new-folder-button-pane"
                    variant="contained"
                    onClick={this.handleCreateSubFolder}
                >
                    <Icon>create_new_folder</Icon>{t("Create Folder")}
                </CreateFolderButton>
            ) : null;
        }

        return (
            <>
                {canCreateSubfolder && (
                    <CreateFolderButton
                        variant="contained"
                        id="new-folder-button-pane"
                        onClick={this.handleCreateSubFolder}
                    >
                        {t("Create Folder")}
                    </CreateFolderButton>
                )}
                <CreatePresentationButtonContainer
                    onMouseEnter={this.handleMouseEnterCreatePresentationButton}
                    onMouseLeave={this.handleMouseLeaveCreatePresentationButton}
                >
                    <BlueButton
                        id="new-presentation-button-pane"
                        onClick={() => this.handleCreateNewPresentationFromHeader(CreatePresentationDialogPaneType.BLANK_PRESENTATION)}
                    >
                        {t("Create presentation")}
                    </BlueButton>
                    <Transition
                        in={!!showCreateMenu}
                        timeout={300}
                    >
                        {state => (
                            <PopupMenuMask state={state}>
                                <CreatePopupMenu state={state}>
                                    <CreatePopupMenuItem
                                        disabled={canUpgradeToCreateResources}
                                        onClick={() => this.handleCreateNewPresentationFromHeader(CreatePresentationDialogPaneType.BLANK_PRESENTATION)}
                                    >
                                        <Icon>add_circle</Icon>{t("Blank Presentation")}
                                    </CreatePopupMenuItem>
                                    {(designerBotAvailable || canUpgradeToUseDesignerBot) && (
                                        <>
                                            <Divider />
                                            <CreatePopupMenuItem onClick={() => {
                                                if (canUpgradeToUseDesignerBot) {
                                                    ShowDialog(ChangePlanDialog, {
                                                        featuredPlan: WorkspacePlanTier.PRO,
                                                        analytics: { cta: "generate_with_ai" }
                                                    });
                                                } else {
                                                    this.handleCreateNewPresentationFromHeader(CreatePresentationDialogPaneType.GENERATE_WITH_AI);
                                                }
                                            }}>
                                                {canUpgradeToUseDesignerBot
                                                    ? <UpgradeIcon style={{ width: 18, height: 18 }} />
                                                    : <AiIconCircle size={18} onClick={() => {}} />}
                                                {t("Create with AI...")}
                                            </CreatePopupMenuItem>
                                            <Divider />
                                        </>
                                    )}
                                    {((sharedResourcesAvailable || canUpgradeToUseSharedResources)) && (
                                        <CreatePopupMenuItem onClick={() => this.handleCreateNewPresentationFromHeader(CreatePresentationDialogPaneType.TEAM_TEMPLATES)}>
                                            {canUpgradeToUseSharedResources
                                                ? <UpgradeIcon style={{ width: 18, height: 18 }} />
                                                : <Icon>video_library</Icon>
                                            }
                                            {t("From Team Template")}
                                        </CreatePopupMenuItem>
                                    )}
                                    {starterTemplatesAvailable && (
                                        <CreatePopupMenuItem onClick={() => this.handleCreateNewPresentationFromHeader(CreatePresentationDialogPaneType.STARTER_TEMPLATES)}>
                                            <Icon>lightbulb</Icon>{t("From Starter Template")}
                                        </CreatePopupMenuItem>
                                    )}
                                </CreatePopupMenu>
                            </PopupMenuMask>
                        )}
                    </Transition>
                </CreatePresentationButtonContainer>
            </>
        );
    }

    renderItems() {
        const { readOnly, filteredPresentations, filter, sort, viewType, searchQuery, isPerformingSearch, resourceFolders, resources } = this.props;
        const aiBullets = [
            t("Create presentations in one click with AI"),
            t("Hundreds of Smart Slide templates"),
            t("Create your branded theme"),
            t("Be a presentation rockstar, make beautiful slides in minutes")
        ];

        const Content = (() => {
            if (filteredPresentations.length == 0) {
                switch (filter.type) {
                    case PresentationFilters.ALL_PRESENTATIONS:
                    case PresentationFilters.OWNED_BY_ME:
                    case PresentationFilters.RECENT:
                        //currently readOnly is set to true when viewing presentation library from CopySlidePane.
                        if (readOnly || searchQuery) {
                            return <NoMatchNotice />;
                        } else {
                            if (app.isConstrained) {
                                return (
                                    <Notice
                                        title={t("You have no presentations in this workspace.")}
                                        message={t("Open Beautiful.ai in your desktop browser to create one.")}
                                    />
                                );
                            } else {
                                if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE)) {
                                    return (
                                        <ResourceNotice
                                            image={getStaticUrl("/images/notice-banner/library.svg")}
                                            title={t("There are no presentations in this workspace.")}
                                            message={t("You are a guest in this workspace. You can find presentations shared with you here.")}
                                        />
                                    );
                                } else if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, PermissionActionType.CREATE)) {
                                    return (
                                        <ResourceNotice
                                            image={getStaticUrl("/images/notice-banner/library.svg")}
                                            title={t("You don't have any presentations yet.")}
                                            message={
                                                <>
                                                    <FlexBox>{t("Create presentations in minutes with automated design.")}</FlexBox>
                                                    <div style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "1fr 1fr",
                                                        gap: "15px",
                                                        marginTop: "10px",
                                                        backgroundColor: "#F7FAFC",
                                                        padding: "15px",
                                                        borderRadius: "2px",
                                                        justifyItems: "start"
                                                    }}>
                                                        {aiBullets.map(text => (
                                                            <FlexBox key={text}>
                                                                <StyledRoundedIcon color="primary" />
                                                                <span>{text}</span>
                                                            </FlexBox>
                                                        ))}
                                                    </div>
                                                </>
                                            }
                                        />
                                    );
                                }
                                return (
                                    <Notice
                                        title={t("Click Create Presentation to get started.")}
                                    />
                                );
                            }
                        }
                    case PresentationFilters.SHARED_WITH_ME:
                        return (
                            <Notice
                                title={t("No one has shared a presentation with you.")}
                                message={(
                                    <PresentationLibraryShareNoticeMessage />
                                )}
                            />
                        );
                    case PresentationFilters.TRASH:
                        return (
                            <Notice
                                title={t("Trash is empty")}
                            />
                        );
                    case PresentationFilters.FOLDER:
                        return (
                            <Notice
                                title={t("This folder is empty.")}
                                message={!app.isConstrained && t("You can drag presentations into this folder to add them.")}
                            />
                        );
                    case PresentationFilters.TEAM_FOLDER:
                        return (
                            <ResourceNotice
                                image={getStaticUrl(`/images/notice-banner/team-folders.svg`)}
                                title={t("Organize your work in shared Team Folders.")}
                                message={t("Collaborate seamlessly and keep your team's presentations organized and easy to find with Team Folders.")}
                            />
                        );
                    default:
                        return (
                            <Notice
                                title={t("No presentations found")}
                            />
                        );
                }
            }

            if (viewType === "grid") {
                return (
                    <PresentationLibraryGrid
                        readOnly={readOnly}
                        resourceFolders={resourceFolders}
                        resources={resources}
                        onNavigateToFolder={this.handleSetFilter}
                        onSelectedPresentations={this.handleSelectedPresentations}
                        onDoubleClick={this.handleDoubleClickedPresentation}
                        onStartDragItems={this.handleStartDragItems}
                        onDropDragItems={this.handleDropDragItems}
                    />
                );
            }

            return (
                <PresentationLibraryList
                    readOnly={readOnly}
                    resourceFolders={resourceFolders}
                    resources={resources}
                    onNavigateToFolder={this.handleSetFilter}
                    onSort={this.handleSort}
                    onSelectedPresentations={this.handleSelectedPresentations}
                    onDoubleClick={this.handleDoubleClickedPresentation}
                    onStartDragItems={this.handleStartDragItems}
                    onDropDragItems={this.handleDropDragItems}
                />
            );
        })();

        return (<>
            <FetchingClickShield backgroundColor="rgb(238 238 238)" visible={isPerformingSearch} />
            {Content}
        </>);
    }
}

// Desktop version
class InnerPresentationLibraryPaneDesktop extends PresentationLibraryPaneBase {
    render() {
        const {
            readOnly,
            onCloseDialog,
            showCloseIcon,
            showSidebar,
            onHideSidebar,
            onSwitchType,
            sideBarWidth,
            isSideBarContentVisible,
            onResizeSidebar,
            className,
            showLibraryViewSelect = true,
            isMyPresentationsOnly = false,
            filter,
            isInitialized,
            sort,
            viewType,
            onReady = () => { },
            resources,
            presentations,
            isLoadingPresentations
        } = this.props;
        const { draggedItems } = this.state;

        const shouldShowSkeleton = isLoadingPresentations;

        if (!isInitialized) {
            return (<UIPane>
                <Spinner />
            </UIPane>);
        }

        if (window.isZoomApp) {
            filter.type = PresentationFilterType.ALL_PRESENTATIONS;
        }

        const { title, subFolders, folderPathFolders } = this.getFilterData();

        const canManageSharedSlides =
            !app.integrator &&
            !readOnly &&
            WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;

        const TrashHeader = filter.type == PresentationFilters.TRASH ? (
            <FlexBox middle right fillWidth style={{ paddingRight: 40 }}>
                <StyledWarningIcon>warning</StyledWarningIcon>
                <Gap10 />
                <WarningBox>
                    {t("Items in your trash will be permanently deleted after 30 days")}
                </WarningBox>
                <Gap20 />
                <WarningButton
                    // @ts-ignore
                    onClick={this.handleEmptyTrash}>
                    {t("Empty Trash")}
                </WarningButton>
            </FlexBox>
        ) : null;
        const canCreateResources = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCES].create;
        const sideBarPanelWidth = isSideBarContentVisible
            ? sideBarWidth + PRESENTATION_LIBRARY_VIEW_SELECT_WIDTH
            : PRESENTATION_LIBRARY_VIEW_SELECT_WIDTH;
        const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, "use");
        const isBasic = WorkspaceController.plan.tier === WorkspacePlanTier.BASIC;

        return (
            // @ts-ignore
            <UIPaneWithSideBar className={className ?? ""}>
                <OnMountedReporter onMounted={onReady} />
                {!window.isZoomApp &&
                    <UIPaneSideBar
                        // @ts-ignore
                        show={showSidebar}
                        onHide={onHideSidebar}
                        style={{ width: sideBarPanelWidth }}
                        onResize={isSideBarContentVisible
                            ? width => onResizeSidebar(Math.max(0, width - PRESENTATION_LIBRARY_VIEW_SELECT_WIDTH))
                            : undefined}
                    >
                        <PresentationLibrarySideBar
                            libraryView={LibraryView.LIBRARY}
                            onSwitchType={onSwitchType}
                            isMyPresentationsOnly={isMyPresentationsOnly}
                            isSideBarContentVisible={isSideBarContentVisible}
                            presentations={presentations}
                            filter={filter}
                            onSetFilter={this.handleSetFilter}
                            readOnly={readOnly}
                            draggedItems={draggedItems}
                            canEditLibraryItems={canManageSharedSlides}
                            onHideSidebar={onHideSidebar}
                        />
                    </UIPaneSideBar>
                }

                {/* @ts-ignore */}
                <UIPaneContents style={{
                    paddingLeft: 30,
                    paddingRight: 30,
                    paddingBottom: 0,
                    paddingTop: 24
                }}>
                    {
                        showCloseIcon &&
                        <CloseButtonContainer>
                            <Icon onClick={onCloseDialog}>
                                close
                            </Icon>
                        </CloseButtonContainer>
                    }

                    <DesktopLibraryContentStack>
                        {window.roomID && (
                            window.isZoomApp
                                ? <RemoteControlBanner blue>{t("Select presentation to share in Zoom meeting.")}</RemoteControlBanner>
                                : <RemoteControlBanner>{t("Select a presentation to Remote Play")}</RemoteControlBanner>
                        )}
                        {!window.roomID && window.isZoomApp && (
                            <RemoteControlBanner blue>{t("Select a presentation to preview in player, please use the browser for full access.")}</RemoteControlBanner>
                        )}

                        {shouldShowSkeleton && <LibrarySkeleton />}

                        {!shouldShowSkeleton && <>
                            {filter.type != PresentationFilters.TRASH
                                ? (
                                    <PresentationListHeader
                                        // @ts-ignore
                                        leadingContent={this.renderHeaderLeadingContent(title, folderPathFolders)}
                                        headerAction={this.renderHeaderAction()}
                                        onSearch={this.handleSearch}
                                        viewType={viewType}
                                        onSetViewType={this.handleSetViewType}
                                        sortOptions={this.sortOptions}
                                        onSetSort={this.handleSort}
                                        onSearchBarOpen={this.handleSearchBarOpen}
                                        sort={sort}
                                        searchPlaceholder={t("Search presentations")}
                                        isBasic={isBasic}
                                    />
                                )
                                : (
                                    <FlexBox left gap={20} middle style={{ minHeight: 65 }}>
                                        <HeaderTitle>{title}</HeaderTitle>
                                        <FlexSpacer />
                                        {TrashHeader}
                                    </FlexBox>
                                )}
                            {subFolders.length > 0 &&
                                <SubFolderContainer>
                                    {filter.subFolderId &&
                                        <IconButton size="small"
                                            onMouseUp={() => this.handleSubFolderMouseUp(null)}><Icon>arrow_upward</Icon></IconButton>
                                    }
                                    {subFolders.map(subFolder => (
                                        <SubFolder
                                            key={subFolder.id}
                                            onMouseUp={() => this.handleSubFolderMouseUp(subFolder.id)}
                                            isSelected={filter.subFolderId === subFolder.id}
                                        >
                                            <Icon>folder_open</Icon>
                                            <Gap10 />
                                            {subFolder.name}
                                            <Gap5 />
                                            <span>({resources.filter(r => r.folderIds.includes(subFolder.id)).length})</span>
                                        </SubFolder>)
                                    )}
                                </SubFolderContainer>
                            }
                            <ScrollBox id="presentation_list" ref={this.scrollRef}
                                fillWidth
                                style={{ height: 0, flexGrow: 2, position: "relative" }}
                            >
                                {this.renderItems()}
                            </ScrollBox>
                        </>}
                    </DesktopLibraryContentStack>
                </UIPaneContents>
                <ShortcutPanel />
            </UIPaneWithSideBar>
        );
    }
}

// Mobile version
class InnerPresentationLibraryPaneMobile extends PresentationLibraryPaneBase {
    render() {
        const {
            readOnly,
            onCloseDialog,
            showCloseIcon,
            showSidebar,
            onHideSidebar,
            onSwitchType,
            sideBarWidth,
            isMyPresentationsOnly = false,
            isSideBarContentVisible,
            onResizeSidebar,
            className,
            filter,
            isInitialized,
            sort,
            viewType,
            onReady = () => { },
            presentations,
            isLoadingPresentations
        } = this.props;
        const { draggedItems, infoBannerClosed } = this.state;

        const shouldShowSkeleton = isLoadingPresentations;

        if (!isInitialized) {
            return (<UIPane>
                <Spinner />
            </UIPane>);
        }

        if (window.isZoomApp) {
            filter.type = PresentationFilterType.ALL_PRESENTATIONS;
        }

        const { title, folderPathFolders } = this.getFilterData();

        const canManageSharedSlides =
            !app.integrator &&
            !readOnly &&
            WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;

        const TrashHeader = filter.type == PresentationFilters.TRASH ? (
            <FlexBox middle right fillWidth style={{ paddingRight: 40 }}>
                <StyledWarningIcon>warning</StyledWarningIcon>
                <Gap10 />
                <WarningBox>
                    {t("Items in your trash will be permanently deleted after 30 days")}
                </WarningBox>
                <Gap20 />
                <WarningButton
                    // @ts-ignore
                    onClick={this.handleEmptyTrash}>
                    {t("Empty Trash")}
                </WarningButton>
            </FlexBox>
        ) : null;

        return (
            // @ts-ignore
            <UIPaneWithSideBar className={className ?? ""}>
                <OnMountedReporter onMounted={onReady} />
                {!window.isZoomApp &&
                    <UIPaneSideBar
                        // @ts-ignore
                        show={showSidebar}
                        onHide={onHideSidebar}
                        style={{ width: sideBarWidth }}
                        onResize={onResizeSidebar}
                    >
                        <PresentationLibrarySideBar
                            libraryView={LibraryView.LIBRARY}
                            onSwitchType={onSwitchType}
                            isMyPresentationsOnly={isMyPresentationsOnly}
                            isSideBarContentVisible={isSideBarContentVisible}
                            presentations={presentations}
                            filter={filter}
                            onSetFilter={this.handleSetFilter}
                            readOnly={readOnly}
                            draggedItems={draggedItems}
                            canEditLibraryItems={canManageSharedSlides}
                            onHideSidebar={onHideSidebar}
                        />
                    </UIPaneSideBar>
                }
                <UIPaneContents>
                    {
                        showCloseIcon &&
                        <CloseButtonContainer isMobile>
                            <Icon onClick={onCloseDialog}>
                                close
                            </Icon>
                        </CloseButtonContainer>
                    }

                    {window.roomID && (
                        window.isZoomApp
                            ? <RemoteControlBanner blue>{t("Select presentation to share in Zoom meeting.")}</RemoteControlBanner>
                            : <RemoteControlBanner>{t("Select a presentation to Remote Play")}</RemoteControlBanner>
                    )}
                    {!window.roomID && window.isZoomApp && (
                        <RemoteControlBanner blue>{t("Select a presentation to preview in player, please use the browser for full access.")}</RemoteControlBanner>
                    )}

                    {shouldShowSkeleton && <LibrarySkeleton />}

                    {/* @ts-ignore */}
                    {!shouldShowSkeleton && <>
                        {/* Scrollable content container */}
                        {/* @ts-ignore */}
                        <HiddenScrollBox id="presentation_list" ref={this.scrollRef}
                            fillWidth
                            style={{
                                height: 0,
                                flexGrow: 2,
                                position: "relative",
                                overflowY: "auto"
                            }}
                        >
                            {/* Content that scrolls away */}
                            <InfoBannerContainer>
                                {!infoBannerClosed && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && (<div>
                                    <InfoBanner
                                        bodyText={t("Create with AI to get started. Switch to desktop to edit and make it your own.")}
                                        onClose={() => this.handleInfoBannerClose()}
                                    />
                                </div>)}
                                {!window.isZoomApp && // @ts-ignore
                                    <FlexBox middle>
                                        <div style={{
                                            width: "100%",
                                            marginTop: 0,
                                            marginRight: 0
                                        }}>{this.renderHeaderLeadingContent(title, folderPathFolders)}</div>
                                    </FlexBox>
                                }

                                {WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use &&
                                    <StyledBAIButton
                                        large
                                        ai
                                        onClick={() => browserHistory.push("/create-presentation/generate-with-ai")}>
                                        <ButtonContent>
                                            <AiIcon onClick={() => browserHistory.push("/create-presentation/generate-with-ai")} />
                                            {t("Create Presentation with AI...")}
                                        </ButtonContent>
                                    </StyledBAIButton>

                                }
                            </InfoBannerContainer>

                            {/* Sticky search bar */}
                            <FlexBox fillWidth horizontalAlign="start" style={{
                                position: "sticky",
                                top: 0,
                                padding: "24px 20px",
                                // To make sure the search bar will always be on top of the presentation items
                                // even when the image is still loading
                                zIndex: 1001,
                                backgroundColor: "white",
                            }}>
                                {
                                    filter.type != PresentationFilters.TRASH &&
                                    <StyledPresentationListHeader
                                        headerAction={this.renderHeaderAction()}
                                        onSearch={this.handleSearch}
                                        viewType={viewType}
                                        onSetViewType={this.handleSetViewType}
                                        sortOptions={this.sortOptions}
                                        onSetSort={this.handleSort}
                                        onSearchBarOpen={this.handleSearchBarOpen}
                                        sort={sort}
                                        searchPlaceholder={t("Search presentations")}
                                    />
                                }
                                {TrashHeader}
                            </FlexBox>

                            {/* Presentation items */}
                            <StyledRenderItemsContainer>
                                {this.renderItems()}
                                <Gap30 />
                            </StyledRenderItemsContainer>
                        </HiddenScrollBox>
                    </>}
                </UIPaneContents>
                <ShortcutPanel />
            </UIPaneWithSideBar>
        );
    }
}

// Main component that decides which version to render
class InnerPresentationLibraryPane extends Component<PresentationLibraryPaneProps, InnerPresentationLibraryPaneState> {
    render() {
        if (app.isMobileOrTablet) {
            return <InnerPresentationLibraryPaneMobile {...this.props} />;
        }
        return <InnerPresentationLibraryPaneDesktop {...this.props} />;
    }
}

export const PresentationLibraryPane =
    PresentationLibraryController.withState(
        withFirebaseUser(
            AppController.withState(
                WorkspaceController.withState(
                    InnerPresentationLibraryPane
                )
            )
        )
    );
